import { useEffect, useState } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import Ordersdetails from '../comp/OrdersDetails.jsx'


export const OrderReport = ({ reportRange, rawData }) => {
    const theme = useTheme();

    const [chartData, setChartData] = useState([]);
    const [topItems, setTopItems] = useState([]);
    const [orderSummary, setOrderSummary] = useState({
        total: 0,
        dineIn: 0,
        takeOut: 0,
        online: 0,
        voided: 0,
        completed: 0,
    });

    useEffect(() => {

        if (rawData) {
            // 1. Chart by hour
            const hourlyMap = {};
            rawData.completedOrders.forEach(order => {
                const hour = new Date(order.startTime).getHours();
                hourlyMap[hour] = (hourlyMap[hour] || 0) + 1;
            });

            const chart = Object.entries(hourlyMap).map(([hour, count]) => ({
                hour: `${hour}:00`,
                orders: count
            }));

            // 2. Order summary
            const summary = {
                total: rawData.orderNumber,
                dineIn: rawData.completedOrders.filter(o => o.type === 0).length,
                takeOut: rawData.completedOrders.filter(o => o.type === 1).length,
                online: rawData.completedOrders.filter(o => o.type === 2).length,
                voided: rawData.cancelOrders?.length || 0,
                completed: rawData.completedOrders.length,
            };

            // 3. Top Items
            const itemMap = {};
            rawData.completedOrders.forEach(order => {
                order.orders.forEach(o => {
                    o.order.forEach(item => {
                        itemMap[item.item] = (itemMap[item.item] || 0) + item.quantity;
                    });
                });
            });

            const items = Object.entries(itemMap)
                .map(([item, qty]) => ({ item, qty }))
                .sort((a, b) => b.qty - a.qty)
                .slice(0, 10);

            setChartData(chart);
            setOrderSummary(summary);
            setTopItems(items);
        } else {
            setChartData([]);
            setTopItems([]);
            setOrderSummary({
                total: 0,
                dineIn: 0,
                takeOut: 0,
                online: 0,
                voided: 0,
                completed: 0,
            });
        }

    }, [reportRange, rawData]);

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            {/* 1. Order Timeline */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders by Hour
                </Typography>
                <LineChart width={600} height={250} data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="orders" stroke={theme.palette.primary.main} />
                </LineChart>
            </Paper>

            
            <Grid container spacing={3}>
                {/* 2. Order Summary */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#fff3e0" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <Table>
                            <TableBody>
                                <TableRow><TableCell>Total Orders</TableCell><TableCell align="right">{orderSummary.total}</TableCell></TableRow>
                                <TableRow><TableCell>Completed</TableCell><TableCell align="right">{orderSummary.completed}</TableCell></TableRow>
                                <TableRow><TableCell>Voided</TableCell><TableCell align="right">{orderSummary.voided}</TableCell></TableRow>
                                <TableRow><TableCell>Dine In</TableCell><TableCell align="right">{orderSummary.dineIn}</TableCell></TableRow>
                                <TableRow><TableCell>Take Out</TableCell><TableCell align="right">{orderSummary.takeOut}</TableCell></TableRow>
                                <TableRow><TableCell>Online</TableCell><TableCell align="right">{orderSummary.online}</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                {/* 3. Top Items */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e3f2fd" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Top Selling Items
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Quantity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topItems.map(row => (
                                    <TableRow key={row.item}>
                                        <TableCell>{row.item}</TableCell>
                                        <TableCell align="right">{row.qty}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 3, my: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom>
                    Orders Details
                </Typography>

                <Ordersdetails orders={rawData?.completedOrders || []} showPagination={true}></Ordersdetails>
                
            </Paper>
        </Container>
    );
};
